import React, { useState } from 'react';
import {
  Drawer,
  Stack,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Grid,
  Collapse,
  TextField,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const UnitDetailDrawer = ({ unit, open, onClose }) => {
  const [openSections, setOpenSections] = useState({});

  const handleToggle = (section) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          padding: 3,
          width: '100%', // Ensures the Stack takes up the full width of the Drawer
          minWidth: 600,
          maxWidth: 1000, // Limits the width of the Drawer
          height: '100%', // Ensures the Stack takes up the full height of the Drawer
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            {unit?.Name}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ position: 'absolute', right: 0, borderRadius: 1, ml: 1 }}
          >
            <CloseIcon sx={{ color: 'gray' }} />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                fontSize="0.9rem"
                color="text.secondary"
              >
                {unit?.UnitType.Description}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" fontSize="0.9rem" fontWeight="bold">
                Unit ID
              </Typography>
              <Typography variant="body1" fontSize="0.9rem">
                {unit?.UnitId}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                fontSize="0.9rem"
                fontWeight="bold"
                color="text.secondary"
              >
                Type
              </Typography>
              <Typography variant="body1" fontSize="0.9rem">
                {unit?.UnitType.Name}
              </Typography>
            </Box>
          </Grid>

          {/* Unit Sensors Section */}
          {unit?.UnitSensors?.map((sensor, index) => (
            <Grid item xs={12} key={sensor.UnitSensorId}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => handleToggle(`UnitSensor_${index}`)}
              >
                <Typography
                  variant="body1"
                  fontSize="0.9rem"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {sensor.UnitTypeInput.Name}
                </Typography>
                {openSections[`UnitSensor_${index}`] ? (
                  <ExpandLessIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'hsl(0, 0%, 20%)'
                          : 'hsl(0, 0%, 85%)',
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'hsl(0, 0%, 20%)'
                          : 'hsl(0, 0%, 85%)',
                    }}
                  />
                )}
              </Box>
              <Collapse in={openSections[`UnitSensor_${index}`]}>
                <List component="div" disablePadding>
                  <ListItem sx={{ pl: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontSize="0.9rem"
                            fontWeight="bold"
                            color={(theme) =>
                              theme.palette.mode === 'light'
                                ? 'hsl(0, 0%, 20%)'
                                : 'hsl(0, 0%, 85%)'
                            }
                          >
                            Custom Name
                          </Typography>
                          <Typography variant="body1" fontSize="0.9rem">
                            {sensor.CustomName}
                          </Typography>
                        </Box>
                      </Grid>
                      {sensor.ApiInput && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body1"
                              fontSize="0.9rem"
                              fontWeight="bold"
                              color={(theme) =>
                                theme.palette.mode === 'light'
                                  ? 'hsl(0, 0%, 20%)'
                                  : 'hsl(0, 0%, 85%)'
                              }
                            >
                              Api Input
                            </Typography>
                            <Typography variant="body1" fontSize="0.9rem">
                              {sensor.ApiInput.toString()}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {sensor.ExternalSensor && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              variant="body1"
                              fontSize="0.9rem"
                              fontWeight="bold"
                              color={(theme) =>
                                theme.palette.mode === 'light'
                                  ? 'hsl(0, 0%, 20%)'
                                  : 'hsl(0, 0%, 85%)'
                              }
                            >
                              External Sensor
                            </Typography>
                            <Typography variant="body1" fontSize="0.9rem">
                              {sensor.ExternalSensor}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {sensor.UnitTypeInput && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              handleToggle(`UnitTypeInput_${index}`)
                            }
                          >
                            <Typography
                              variant="body1"
                              fontSize="0.9rem"
                              fontWeight="bold"
                              color={(theme) =>
                                theme.palette.mode === 'light'
                                  ? 'hsl(0, 0%, 20%)'
                                  : 'hsl(0, 0%, 85%)'
                              }
                            >
                              Unit Type Input
                            </Typography>
                            {openSections[`UnitTypeInput_${index}`] ? (
                              <ExpandLessIcon
                                sx={{
                                  color: (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                              />
                            ) : (
                              <ExpandMoreIcon
                                sx={{
                                  color: (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                              />
                            )}
                          </Box>
                          <Collapse in={openSections[`UnitTypeInput_${index}`]}>
                            <List component="div" disablePadding>
                              <ListItem sx={{ pl: 2 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontSize="0.9rem"
                                        fontWeight="bold"
                                        color={(theme) =>
                                          theme.palette.mode === 'light'
                                            ? 'hsl(0, 0%, 20%)'
                                            : 'hsl(0, 0%, 85%)'
                                        }
                                      >
                                        Description
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        fontSize="0.9rem"
                                      >
                                        {sensor.UnitTypeInput.Description}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontSize="0.9rem"
                                        fontWeight="bold"
                                        color={(theme) =>
                                          theme.palette.mode === 'light'
                                            ? 'hsl(0, 0%, 20%)'
                                            : 'hsl(0, 0%, 85%)'
                                        }
                                      >
                                        Name
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        fontSize="0.9rem"
                                      >
                                        {sensor.UnitTypeInput.Name}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontSize="0.9rem"
                                        fontWeight="bold"
                                        color={(theme) =>
                                          theme.palette.mode === 'light'
                                            ? 'hsl(0, 0%, 20%)'
                                            : 'hsl(0, 0%, 85%)'
                                        }
                                      >
                                        Unit Type Input ID
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        fontSize="0.9rem"
                                      >
                                        {sensor.UnitTypeInput.UnitTypeInputId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </ListItem>
                            </List>
                          </Collapse>
                        </Grid>
                      )}
                    </Grid>
                  </ListItem>
                </List>
              </Collapse>
            </Grid>
          ))}

          {/* Recorded Data Types Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => handleToggle('RecordedDataTypes')}
            >
              <Typography
                variant="body1"
                fontSize="0.9rem"
                fontWeight="bold"
                color={(theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 20%)'
                    : 'hsl(0, 0%, 85%)'
                }
              >
                Recorded Data Types
              </Typography>
              {openSections['RecordedDataTypes'] ? (
                <ExpandLessIcon
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'hsl(0, 0%, 20%)'
                        : 'hsl(0, 0%, 85%)',
                  }}
                />
              ) : (
                <ExpandMoreIcon
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'hsl(0, 0%, 20%)'
                        : 'hsl(0, 0%, 85%)',
                  }}
                />
              )}
            </Box>
            <Collapse in={openSections['RecordedDataTypes']}>
              <List component="div" disablePadding>
                {unit?.RecordedDataTypes?.map((dataType, index) => (
                  <ListItem key={index} sx={{ pl: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontSize="0.9rem"
                            fontWeight="bold"
                            color={(theme) =>
                              theme.palette.mode === 'light'
                                ? 'hsl(0, 0%, 20%)'
                                : 'hsl(0, 0%, 85%)'
                            }
                          >
                            {dataType.UnitTypeInput.Name}
                          </Typography>
                          <Typography variant="body1" fontSize="0.9rem">
                            {dataType.DataTypes.join(', ')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                fontSize="0.9rem"
                fontWeight="bold"
                color={(theme) =>
                  theme.palette.mode === 'light'
                    ? 'hsl(0, 0%, 20%)'
                    : 'hsl(0, 0%, 85%)'
                }
              >
                Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {unit?.Tags?.map((tag) => (
                  <Chip
                    key={tag.TagId}
                    label={tag.Text}
                    size="small"
                    sx={{ borderRadius: 1, marginLeft: 0.5, marginBottom: 0.5 }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Description Field */}
          <Grid item xs={12}>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize="0.9rem"
              color="text.secondary"
            >
              Description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={unit?.Description || ''}
              InputProps={{
                readOnly: false,
                fontSize: '0.9rem',
              }}
              sx={{
                mt: 1,
                '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after':
                  {
                    display: 'none',
                  },
                '& .MuiInputBase-root': {
                  borderRadius: '5px',
                },
                '& .MuiFilledInput-root': {
                  borderRadius: '5px',
                },
              }}
            />
          </Grid>
        </Grid>
        {/* </ListItem>
        </List> */}
      </Stack>
    </Drawer>
  );
};

export default UnitDetailDrawer;
